import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Tooltip,
  InputAdornment,
  CircularProgress,
  Button,
} from '@mui/material';
import { ArrowBack as BackIcon, Edit as EditIcon } from '@mui/icons-material';
import { CorkboardProvider } from './CorkboardProvider';
import CorkboardCanvas from './CorkboardCanvas';
import CorkboardHotbar from './CorkboardHotbar';
import { selectCorkboardById, addCorkboard, updateCorkboard } from './corkboardsSlice';
import { getCorkboardsUrl } from '../../urls';
import useThemeStyles from '../../hooks/useThemeStyles';
import corkboardService from '../../utils/apiRequests/corkboardService';

/**
 * Normalize backend corkboard data to frontend format
 * Backend uses _id, frontend uses id
 * Backend sends Date objects as ISO strings in JSON, convert to epoch timestamps
 */
const normalizeCorkboard = (backendCorkboard) => {
  if (!backendCorkboard) return null;

  const normalized = {
    ...backendCorkboard,
    id: backendCorkboard.id || backendCorkboard._id?.toString() || backendCorkboard._id,
    projectId: backendCorkboard.projectId?.toString() || backendCorkboard.projectId,
  };

  // Convert Date strings to epoch timestamps
  if (backendCorkboard.createdAt) {
    if (typeof backendCorkboard.createdAt === 'string') {
      normalized.createdAt = new Date(backendCorkboard.createdAt).getTime();
    } else if (backendCorkboard.createdAt instanceof Date) {
      normalized.createdAt = backendCorkboard.createdAt.getTime();
    } else {
      normalized.createdAt = backendCorkboard.createdAt;
    }
  } else {
    normalized.createdAt = Date.now();
  }

  if (backendCorkboard.updatedAt) {
    if (typeof backendCorkboard.updatedAt === 'string') {
      normalized.updatedAt = new Date(backendCorkboard.updatedAt).getTime();
    } else if (backendCorkboard.updatedAt instanceof Date) {
      normalized.updatedAt = backendCorkboard.updatedAt.getTime();
    } else {
      normalized.updatedAt = backendCorkboard.updatedAt;
    }
  } else {
    normalized.updatedAt = Date.now();
  }

  return normalized;
};

/**
 * CorkboardEditorPage - Single corkboard editor with canvas and hotbar
 */
const CorkboardEditorPage = ({ corkboardId }) => {
  const { colors, isDark } = useThemeStyles();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const corkboard = useSelector(selectCorkboardById(corkboardId));
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const debounceTimeoutRef = useRef(null);

  // Load corkboard from backend on mount
  useEffect(() => {
    const loadCorkboard = async () => {
      if (!projectId || !corkboardId) {
        setLoading(false);
        return;
      }

      // If corkboard already exists in Redux, skip loading
      if (corkboard) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setNotFound(false);

      try {
        const response = await corkboardService.getCorkboardById(projectId, corkboardId);

        if (response.error) {
          console.error('Error loading corkboard:', response.error);
          setNotFound(true);
          setLoading(false);
          return;
        }

        if (response.data?.data) {
          const normalizedCorkboard = normalizeCorkboard(response.data.data);
          dispatch(addCorkboard(normalizedCorkboard));
        } else {
          setNotFound(true);
        }
      } catch (err) {
        console.error('Error loading corkboard:', err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    loadCorkboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, corkboardId]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleBack = () => {
    navigate(getCorkboardsUrl(projectId));
  };

  const handleNameChange = async (e) => {
    const newName = e.target.value;
    dispatch(
      updateCorkboard({
        id: corkboardId,
        name: newName,
      })
    );

    // Auto-save name change with debouncing
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await corkboardService.updateCorkboard(projectId, corkboardId, {
          name: newName,
        });

        if (response.error) {
          console.error('Error auto-saving corkboard name:', response.error);
        }
      } catch (err) {
        console.error('Error auto-saving corkboard name:', err);
      }
    }, 1000); // 1 second debounce for name
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          backgroundColor: colors.background.primary,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (notFound || !corkboard) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          backgroundColor: colors.background.primary,
          gap: 2,
        }}
      >
        <Typography sx={{ color: colors.text.secondary }}>
          {notFound ? 'Corkboard not found' : 'Loading corkboard...'}
        </Typography>
        <Button onClick={() => navigate(getCorkboardsUrl(projectId))}>Back to Corkboards</Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          borderBottom: `1px solid ${colors.border.subtle}`,
          backgroundColor: isDark ? colors.paper.background : colors.background.secondary,
          minHeight: 48,
        }}
      >
        <Tooltip
          title="Back to Corkboards"
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: colors.tooltip.background,
                color: colors.tooltip.color,
                border: `1px solid ${colors.tooltip.border}`,
              },
            },
          }}
        >
          <IconButton
            onClick={handleBack}
            size="small"
            sx={{
              color: colors.text.primary,
              '&:hover': {
                backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
              },
            }}
          >
            <BackIcon />
          </IconButton>
        </Tooltip>
        <TextField
          value={corkboard.name || ''}
          onChange={handleNameChange}
          variant="standard"
          placeholder="Untitled Corkboard"
          InputProps={{
            disableUnderline: true,
            endAdornment: (
              <InputAdornment position="end">
                <EditIcon
                  sx={{
                    fontSize: 16,
                    color: colors.text.secondary,
                    opacity: 0.6,
                  }}
                />
              </InputAdornment>
            ),
            sx: {
              fontSize: '1.1rem',
              fontWeight: 'bold',
              color: colors.text.primary,
              '&:hover': {
                backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
              },
              '&.Mui-focused': {
                backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
                outline: `2px solid ${colors.interactive.primary}`,
                outlineOffset: 1,
              },
              '&::placeholder': {
                color: colors.text.muted,
                opacity: 1,
              },
              px: 1,
              py: 0.5,
              borderRadius: 1,
              transition: 'background-color 0.2s, outline 0.2s',
            },
          }}
          sx={{
            minWidth: 200,
            '& input::placeholder': {
              color: colors.text.muted,
              opacity: 1,
            },
          }}
        />
        <Typography variant="caption" sx={{ ml: 'auto', mr: 2, color: colors.text.secondary }}>
          {corkboard.notes?.length || 0} notes • {corkboard.drawings?.length || 0} drawings
        </Typography>
      </Box>

      {/* Canvas Area */}
      <Box sx={{ flexGrow: 1, position: 'relative', overflow: 'hidden' }}>
        <CorkboardProvider corkboardId={corkboardId}>
          <CorkboardCanvas />
          <CorkboardHotbar />
        </CorkboardProvider>
      </Box>
    </Box>
  );
};

CorkboardEditorPage.propTypes = {
  corkboardId: PropTypes.string.isRequired,
};

export default CorkboardEditorPage;
